class StorefrontFilters {
	constructor() {
		this.filtersContainer = document.querySelector('#FiltersContainer');
		this.resultsContainer = document.querySelector('#ResultsContainer');

		if (!this.filtersContainer || !this.resultsContainer) return;

		this.mobileBreakpoint = window.matchMedia('(max-width: 1199px)');
		this.priceTimer = null;
		this.controller = null;
		this.requestId = 0;
		this.currentUrl = window.location.href;

		this.onChange = this.onChange.bind(this);
		this.onSubmit = this.onSubmit.bind(this);
		this.onClick = this.onClick.bind(this);
		this.onPopState = this.onPopState.bind(this);

		this.filtersContainer.addEventListener('change', this.onChange);
		this.filtersContainer.addEventListener('submit', this.onSubmit);
		this.filtersContainer.addEventListener('click', this.onClick);

		window.addEventListener('popstate', this.onPopState);
	}

	get form() {
		return this.filtersContainer.querySelector('[data-filters-form]');
	}

	get sectionId() {
		return this.resultsContainer.dataset.sectionId;
	}

	get isMobile() {
		return this.mobileBreakpoint.matches;
	}

	onChange(event) {
		const input = event.target;

		if (!input.matches('input, select')) return;
		if (this.isMobile) return;

		if (input.matches('[data-filter-price]')) {
			this.debouncePriceUpdate();
			return;
		}

		this.applyFilters();
	}

	onSubmit(event) {
		if (!event.target.matches('[data-filters-form]')) return;

		event.preventDefault();

		clearTimeout(this.priceTimer);
		this.applyFilters();
	}

	onClick(event) {
		const showMoreButton = event.target.closest('[data-filter-show-more]');

		if (
			showMoreButton &&
			this.filtersContainer.contains(showMoreButton)
		) {
			event.preventDefault();
			this.toggleShowMore(showMoreButton);
			return;
		}

		const removeLink = event.target.closest('[data-filter-remove]');

		if (
			removeLink &&
			this.filtersContainer.contains(removeLink)
		) {
			event.preventDefault();

			clearTimeout(this.priceTimer);
			this.render(removeLink.href);
		}
	}

	debouncePriceUpdate() {
		clearTimeout(this.priceTimer);

		this.priceTimer = setTimeout(() => {
			if (!this.isMobile) {
				this.applyFilters();
			}
		}, 500);
	}

	toggleShowMore(button) {
		const group = button.closest('[data-filter-group]');

		if (!group) return;

		const expanded = button.getAttribute('aria-expanded') === 'true';
		const hiddenItems = group.querySelectorAll('.filters__item--more');

		hiddenItems.forEach((item) => {
			item.hidden = expanded;
		});

		button.setAttribute('aria-expanded', String(!expanded));

		const moreLabel = button.querySelector('[data-show-more-label]');
		const lessLabel = button.querySelector('[data-show-less-label]');

		if (moreLabel) moreLabel.hidden = !expanded;
		if (lessLabel) lessLabel.hidden = expanded;
	}

	createUrlFromForm() {
		const url = new URL(window.location.href);
		const form = this.form;

		if (!form) return url;

		// Remove old Shopify filter parameters.
		[...url.searchParams.keys()].forEach((key) => {
			if (key.startsWith('filter.')) {
				url.searchParams.delete(key);
			}
		});

		const formData = new FormData(form);
		const params = new URLSearchParams(formData);

		params.forEach((value, key) => {
			if (key.startsWith('filter.') && value !== '') {
				url.searchParams.append(key, value);
			}
		});

		// Keep the current sorting.
		const sortBy =
			url.searchParams.get('sort_by') ||
			params.get('sort_by');

		if (sortBy) {
			url.searchParams.set('sort_by', sortBy);
		}

		// Preserve Shopify Search parameters.
		[
			'q',
			'options[prefix]',
			'type'
		].forEach((key) => {
			if (!url.searchParams.has(key) && params.has(key)) {
				url.searchParams.set(key, params.get(key));
			}
		});

		// Start from the first page after filtering.
		url.searchParams.delete('page');
		url.searchParams.delete('section_id');

		return url;
	}

	applyFilters() {
		const url = this.createUrlFromForm();
		this.render(url.toString());
	}

	getOpenGroups() {
		return [
			...this.filtersContainer.querySelectorAll('[data-filter-group]')
		]
			.filter((group) => group.open)
			.map((group) => this.getGroupKey(group));
	}

	getGroupKey(group) {
		const input = group.querySelector('input[name]');

		if (input) return input.name;

		return (
			group.querySelector('.filters__summary-label')
				?.textContent.trim() || ''
		);
	}

	restoreOpenGroups(openGroups) {
		const groups = this.filtersContainer.querySelectorAll(
			'[data-filter-group]'
		);

		groups.forEach((group) => {
			group.open = openGroups.includes(this.getGroupKey(group));
		});
	}

	async render(target, { updateHistory = true } = {}) {
		const targetUrl = new URL(target, window.location.href);

		targetUrl.searchParams.delete('section_id');

		// Prevent requests to another origin.
		if (targetUrl.origin !== window.location.origin) {
			window.location.assign(targetUrl.href);
			return;
		}

		const browserUrl =
			`${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;

		if (!this.sectionId) {
			window.location.assign(browserUrl);
			return;
		}

		// Abort previous filtering request.
		if (this.controller) {
			this.controller.abort();
		}

		this.controller = new AbortController();

		const controller = this.controller;
		const requestId = ++this.requestId;
		const openGroups = this.getOpenGroups();

		const requestUrl = new URL(targetUrl);
		requestUrl.searchParams.set('section_id', this.sectionId);

		this.setLoading(true);

		try {
			const response = await fetch(requestUrl.toString(), {
				signal: controller.signal,
				credentials: 'same-origin',
				headers: {
					'X-Requested-With': 'XMLHttpRequest'
				}
			});

			if (!response.ok) {
				throw new Error(
					`Filter request failed: ${response.status}`
				);
			}

			const html = await response.text();

			if (requestId !== this.requestId) return;

			const documentFromResponse = new DOMParser()
				.parseFromString(html, 'text/html');

			const newFilters = documentFromResponse.querySelector(
				'#FiltersContainer'
			);

			const newResults = documentFromResponse.querySelector(
				'#ResultsContainer'
			);

			if (!newFilters || !newResults) {
				throw new Error(
					'Filters or results container was not found.'
				);
			}

			// Update the filters.
			this.filtersContainer.innerHTML = newFilters.innerHTML;

			// Restore accordion state.
			this.restoreOpenGroups(openGroups);

			// Update toolbar, products, count and pagination.
			this.resultsContainer.innerHTML = newResults.innerHTML;

			if (updateHistory) {
				window.history.pushState(
					{ filtersUrl: browserUrl },
					'',
					browserUrl
				);
			}

			this.currentUrl = window.location.href;

			// Notify Ella's collection loader and other components.
			document.dispatchEvent(
				new CustomEvent('filters:updated', {
					detail: {
						url: browserUrl,
						sectionId: this.sectionId,
						filters: this.filtersContainer,
						results: this.resultsContainer
					}
				})
			);

		} catch (error) {
			if (error.name === 'AbortError') return;
			if (requestId !== this.requestId) return;

			console.error('[StorefrontFilters]', error);

			window.location.assign(browserUrl);

		} finally {
			if (requestId === this.requestId) {
				this.setLoading(false);
				this.controller = null;
			}
		}
	}

	setLoading(loading) {
		const filters = this.filtersContainer.querySelector(
			'[data-filters]'
		);

		if (filters) {
			filters.classList.toggle('is-loading', loading);
			filters.setAttribute('aria-busy', String(loading));
		}

		this.resultsContainer.classList.toggle('is-loading', loading);
		this.resultsContainer.setAttribute(
			'aria-busy',
			String(loading)
		);
	}

	onPopState() {
		clearTimeout(this.priceTimer);

		this.render(window.location.href, {
			updateHistory: false
		});
	}
}

/**
 * Search-only toolbar.
 *
 * Keeps the Ella toolbar appearance without using
 * the collection-specific sort/view event handlers.
 */

class SearchToolbar {
	constructor() {
		this.root = document.querySelector(
			'.search-page[data-search-page]'
		);

		if (!this.root) return;

		this.isUpdatingLimit = false;
		this.layoutFrame = null;

		this.columns = {
			1: 'col-12',
			2: 'col-6',
			3: 'col-6 col-md-4',
			4: 'col-6 col-md-4 col-lg-3',
			5: 'col-6 col-md-4 col-lg-3 col5'
		};

		const grid = this.root.querySelector('#results-grid');
		const activeButton = this.root.querySelector(
			'[data-search-view-as] [data-col].active'
		);

		// Remember the selected layout.
		this.selectedColumn = activeButton
			? Number(activeButton.dataset.col)
			: grid?.classList.contains('products-list')
				? 1
				: null;

		this.onClick = this.onClick.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.onResultsUpdated = this.onResultsUpdated.bind(this);

		document.addEventListener('click', this.onClick);
		document.addEventListener('keydown', this.onKeyDown);

		// Reapply the layout after AJAX filter updates.
		document.addEventListener(
			'filters:updated',
			this.onResultsUpdated
		);

		// Also handle cards appended by Show More / pagination.
		const resultsContainer = this.root.querySelector(
			'#ResultsContainer'
		);

		if (resultsContainer && 'MutationObserver' in window) {
			this.observer = new MutationObserver(() => {
				this.scheduleLayoutUpdate();
			});

			this.observer.observe(resultsContainer, {
				childList: true,
				subtree: true
			});
		}
	}

	onClick(event) {
		const sortOption = event.target.closest(
			'[data-search-sortby] [data-href]'
		);

		if (sortOption && this.root.contains(sortOption)) {
			event.preventDefault();
			event.stopPropagation();

			this.sortProducts(sortOption.dataset.href);
			return;
		}

		const limitOption = event.target.closest(
			'[data-search-limited-view] [data-value]'
		);

		if (limitOption && this.root.contains(limitOption)) {
			event.preventDefault();
			event.stopPropagation();

			this.changeLimit(limitOption.dataset.value);
			return;
		}

		const viewOption = event.target.closest(
			'[data-search-view-as] [data-col]'
		);

		if (viewOption && this.root.contains(viewOption)) {
			event.preventDefault();
			event.stopPropagation();

			this.changeView(viewOption);
		}
	}

	onKeyDown(event) {
		if (event.key !== 'Enter' && event.key !== ' ') return;

		const viewOption = event.target.closest(
			'[data-search-view-as] [data-col]'
		);

		if (viewOption && this.root.contains(viewOption)) {
			event.preventDefault();
			this.changeView(viewOption);
		}
	}

	sortProducts(sortBy) {
		if (!sortBy) return;

		const url = new URL(window.location.href);

		url.searchParams.set('sort_by', sortBy);
		url.searchParams.delete('page');
		url.searchParams.delete('section_id');

		window.location.assign(url.href);
	}

	async changeLimit(value) {
		const allowedLimits = ['12', '16', '20', '24', '30', '50'];

		if (!allowedLimits.includes(value)) return;
		if (this.isUpdatingLimit) return;

		this.isUpdatingLimit = true;

		const limitContainer = this.root.querySelector(
			'[data-search-limited-view]'
		);

		if (limitContainer) {
			limitContainer.setAttribute('aria-busy', 'true');
		}

		try {
			const response = await fetch(
				`${window.Shopify?.routes?.root || '/'}cart/update.js`,
				{
					method: 'POST',
					credentials: 'same-origin',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({
						attributes: {
							pagination: value
						}
					})
				}
			);

			if (!response.ok) {
				throw new Error(
					`Pagination update failed: ${response.status}`
				);
			}

			const url = new URL(window.location.href);

			url.searchParams.delete('page');
			url.searchParams.delete('section_id');

			window.location.assign(url.href);

		} catch (error) {
			console.error('[SearchToolbar]', error);

			this.isUpdatingLimit = false;

			if (limitContainer) {
				limitContainer.setAttribute('aria-busy', 'false');
			}
		}
	}

	changeView(button) {
		const column = Number(button.dataset.col);

		if (!this.columns[column]) return;

		// Save the selected layout for future AJAX updates.
		this.selectedColumn = column;

		this.applyView(column);

		if (window.jQuery) {
			window.jQuery(window).trigger('resize');
		}
	}

	applyView(column) {
		if (!this.columns[column]) return;

		const grid = this.root.querySelector('#results-grid');
		const toolbar = this.root.querySelector(
			'[data-search-view-as]'
		);

		if (!grid) return;

		if (toolbar) {
			toolbar.querySelectorAll('[data-col]').forEach((button) => {
				const active = Number(button.dataset.col) === column;

				button.classList.toggle('active', active);
				button.setAttribute('aria-pressed', String(active));
			});
		}

		grid.classList.toggle('products-list', column === 1);
		grid.classList.toggle('products-grid', column !== 1);

		grid.querySelectorAll('.grid-item').forEach((item) => {
			item.classList.remove(
				'col-12',
				'col-6',
				'col-md-4',
				'col-lg-3',
				'col5'
			);

			item.classList.add(
				...this.columns[column].split(' ')
			);
		});
	}

	scheduleLayoutUpdate() {
		if (this.layoutFrame !== null) {
			cancelAnimationFrame(this.layoutFrame);
		}

		this.layoutFrame = requestAnimationFrame(() => {
			this.layoutFrame = null;

			if (this.selectedColumn !== null) {
				this.applyView(this.selectedColumn);
			}
		});
	}

	onResultsUpdated() {
		this.scheduleLayoutUpdate();
	}
}

/**
 * Initialize both components.
 *
 * StorefrontFilters works on Collection and Search.
 * SearchToolbar runs only on Search.
 */
document.addEventListener('DOMContentLoaded', () => {
	new StorefrontFilters();
	new SearchToolbar();
});
